import { memo } from 'react';
import styled from '@emotion/styled';
import { EditActionButtons } from '../presentation/EditActionButtons';

type ChatConfigReadOnlyProps = {
  initialMessage: string;
  questions: string[];
  onEdit: () => void;
  whitelabelEnabled?: boolean;
  primaryColor?: string;
};

const ChatConfigReadOnlyComponent = ({
  initialMessage,
  questions,
  onEdit,
  whitelabelEnabled,
  primaryColor,
}: ChatConfigReadOnlyProps) => {
  return (
    <>
      <Header>
        <Title>Customize Chat Questions</Title>
        <Description>
          Customize the initial message and starter questions shown in the chat interface for this segment&apos;s Digital Twin.
        </Description>
      </Header>

      <Field>
        <FieldLabel>Initial Message</FieldLabel>
        <DisplayContainer>
          <MessageDisplay>
            {initialMessage}
          </MessageDisplay>
          <EditActionButtons
            isEditing={false}
            onEdit={onEdit}
            onSave={NOOP}
            onCancel={NOOP}
            themedColor={whitelabelEnabled && primaryColor ? primaryColor : undefined}
          />
        </DisplayContainer>
      </Field>

      <Field>
        <FieldLabel>Questions</FieldLabel>
        {questions.length > 0 ? (
          <QuestionsList>
            {questions.map((q, i) => (
              <QuestionItem key={i}>{q}</QuestionItem>
            ))}
          </QuestionsList>
        ) : (
          <Description>No questions configured.</Description>
        )}
      </Field>
    </>
  );
};

export const ChatConfigReadOnly = memo(ChatConfigReadOnlyComponent);

// EditActionButtons' onSave/onCancel are only ever invoked while isEditing — never true here.
function NOOP() {
  /* no-op */
}

const Header = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: var(--gray-d);
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-m);
  margin: 0;
`;

const DisplayContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const Field = styled.div`
  margin-top: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--gray-d);
`;

const MessageDisplay = styled.div`
  flex: 1;
  background-color: var(--gray-xl);
  border: 1px solid var(--gray-l);
  border-radius: 4px;
  padding: 16px;
  min-height: 60px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-d);
`;

const QuestionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QuestionItem = styled.li`
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-d);
`;
